import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  User
} from "firebase/auth";
import { auth, database } from "../firebase";
import { ref, set, get } from "firebase/database";

// Types
export interface AuthResult {
  success: boolean;
  user?: User;
  error?: string;
}

// Monitor auth state
export const subscribeToAuthChanges = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Sign in
export const signIn = async (email: string, password: string): Promise<AuthResult> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Sign up
export const signUp = async (email: string, password: string): Promise<AuthResult> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Create user profile
    await createUserProfile(userCredential.user.uid, email);
    
    return { success: true, user: userCredential.user };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Sign out
export const logOut = async (): Promise<AuthResult> => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Reset password
export const resetPassword = async (email: string): Promise<AuthResult> => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Create user profile in Realtime Database
const createUserProfile = async (userId: string, email: string) => {
  try {
    const userRef = ref(database, `users/${userId}`);
    await set(userRef, {
      email: email,
      subscriptionStatus: "free",
      createdAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error("Error creating user profile:", error);
    return false;
  }
};

// Get user subscription status
export const getUserSubscription = async (userId: string): Promise<string> => {
  try {
    const userRef = ref(database, `users/${userId}`);
    const snapshot = await get(userRef);
    
    if (snapshot.exists()) {
      return snapshot.val().subscriptionStatus || "free";
    }
    return "free";
  } catch (error) {
    console.error("Error getting subscription:", error);
    return "free";
  }
}; 